import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Upload, Type, Palette, Ruler, ShoppingBag, Sparkles } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

const GARMENTS = [
  { id: "tshirt", label: "Classic Tee", base: 599 },
  { id: "oversized", label: "Oversized Tee", base: 799 },
  { id: "polo", label: "Polo", base: 899 },
  { id: "hoodie", label: "Hoodie", base: 1499 },
];
const COLORS = [
  { name: "White", hex: "#F8F5EE" },
  { name: "Black", hex: "#0A0A0A" },
  { name: "Cream", hex: "#EFE4CF" },
  { name: "Burgundy", hex: "#8B2E2E" },
  { name: "Olive", hex: "#4C5237" },
  { name: "Navy", hex: "#1B2748" },
];
const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];
const PLACEMENTS = ["Front-Chest", "Front-Center", "Back-Center", "Left-Sleeve"];

export default function Customize() {
  const nav = useNavigate();
  const { addToCart } = useApp();
  useSEO({
    title: "Design Your Own T-Shirt · Custom Print Studio",
    description: "Print your own t-shirt online with Venvay. Add text, upload logos, pick colors and placements. Custom hoodies and sweatshirts too. Shipped in 3–5 days from India.",
    keywords: "print your own t-shirt online, custom tshirt design india, upload logo tshirt, custom hoodie printing india, bulk corporate merchandise",
    canonicalPath: "/customize",
  });
  const [garment, setGarment] = useState(GARMENTS[1]);
  const [color, setColor] = useState(COLORS[1]);
  const [size, setSize] = useState("M");
  const [qty, setQty] = useState(1);
  const [text, setText] = useState("");
  const [textColor, setTextColor] = useState("#F8F5EE");
  const [textSize, setTextSize] = useState(40);
  const [placement, setPlacement] = useState("Front-Chest");
  const [logo, setLogo] = useState(null);
  const fileRef = useRef(null);

  const printFee = (text ? 149 : 0) + (logo ? 199 : 0);
  const unit = garment.base + printFee;
  const total = unit * qty;

  const onUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) return toast.error("Max file size 3 MB");
    const url = URL.createObjectURL(f);
    setLogo({ url, name: f.name });
    toast.success("Design uploaded");
  };

  const addPrintedToCart = () => {
    if (!text && !logo) return toast.error("Add text or upload a design to print");
    const product = {
      id: `custom-${Date.now()}`,
      name: `Custom ${garment.label} · ${color.name}`,
      price: unit,
      images: [logo?.url || "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"],
    };
    addToCart(product, size, qty, `${color.name} · ${placement}${text ? ` · "${text}"` : ""}`);
    toast.success("Added to bag — custom print will be produced in 3–5 days");
    nav("/cart");
  };

  const previewSize = 340;
  const artStyle = useMemo(() => {
    const pos = {
      "Front-Chest": { left: "18%", top: "22%", size: 0.35 },
      "Front-Center": { left: "22%", top: "34%", size: 0.55 },
      "Back-Center": { left: "22%", top: "34%", size: 0.55 },
      "Left-Sleeve": { left: "6%", top: "20%", size: 0.18 },
    }[placement];
    return {
      position: "absolute",
      left: pos.left, top: pos.top,
      width: `${pos.size * 100}%`,
    };
  }, [placement]);

  return (
    <div>
      <div className="bg-[#0F0B08] text-[#F0E5D0] py-12 border-b border-[#D4B25C]/10">
        <div className="max-w-7xl mx-auto px-6">
          <p className="label-tag text-[#D4B25C]/60 mb-4">Design Studio</p>
          <h1 className="font-display text-5xl sm:text-6xl font-black leading-none">
            PRINT YOUR OWN <span className="text-[#D4B25C]">STORY.</span>
          </h1>
          <p className="mt-4 text-white/70 max-w-xl">Add your text, upload your logo, pick your garment. Handled in-house by Venvay's print studio in India.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-[1fr_1fr] gap-12">
        {/* PREVIEW */}
        <div className="lg:sticky lg:top-32 lg:self-start">
          <div className="relative mx-auto rounded-md overflow-hidden grain" style={{ width: previewSize, height: previewSize * 1.25, background: color.hex }}>
            {/* Simple T-shape */}
            <svg viewBox="0 0 400 500" className="absolute inset-0 w-full h-full">
              <path d="M60 90 L140 40 L180 60 Q200 80 220 60 L260 40 L340 90 L310 170 L280 150 L280 460 L120 460 L120 150 L90 170 Z"
                fill={color.hex} stroke="#0A0A0A" strokeOpacity="0.15" strokeWidth="1.5" />
            </svg>

            {/* Art overlay */}
            <div style={artStyle} className="pointer-events-none">
              {logo && <img src={logo.url} alt="art" className="w-full object-contain" />}
              {text && (
                <div style={{ color: textColor, fontSize: `${textSize * 0.4}px`, textAlign: "center", fontFamily: "Playfair Display, serif", fontWeight: 900, letterSpacing: "0.05em", lineHeight: 1 }}>
                  {text}
                </div>
              )}
            </div>

            <div className="absolute bottom-3 left-3 bg-white/85 text-[10px] tracking-[0.3em] uppercase px-2 py-1 font-bold">
              {placement}
            </div>
          </div>
          <p className="text-center mt-4 text-xs text-muted-foreground">Live preview · Actual print may vary slightly</p>
        </div>

        {/* FORM */}
        <div className="space-y-8">
          <section>
            <p className="label-tag mb-3"><Ruler size={12} className="inline mr-1" /> Garment</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {GARMENTS.map((g) => (
                <button key={g.id} data-testid={`garment-${g.id}`} onClick={() => setGarment(g)}
                  className={`p-3 border text-sm font-semibold transition-colors ${garment.id === g.id ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-black/20 hover:border-[#8B2E2E]"}`}>
                  {g.label}<br /><span className="text-xs opacity-70 font-normal">₹{g.base}</span>
                </button>
              ))}
            </div>
          </section>

          <section>
            <p className="label-tag mb-3"><Palette size={12} className="inline mr-1" /> Garment Color</p>
            <div className="flex gap-2 flex-wrap">
              {COLORS.map((c) => (
                <button key={c.name} data-testid={`color-${c.name}`} onClick={() => setColor(c)} title={c.name}
                  className={`h-11 w-11 rounded-full border-2 transition-transform ${color.name === c.name ? "border-[#8B2E2E] scale-110" : "border-black/10"}`}
                  style={{ background: c.hex }} />
              ))}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{color.name}</p>
          </section>

          <section>
            <p className="label-tag mb-3">Size & Quantity</p>
            <div className="flex flex-wrap gap-2 items-center">
              {SIZES.map((s) => (
                <button key={s} data-testid={`size-${s}`} onClick={() => setSize(s)}
                  className={`h-10 min-w-10 px-3 border text-sm font-semibold ${size === s ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-black/20 hover:border-[#8B2E2E]"}`}>
                  {s}
                </button>
              ))}
              <div className="flex items-center border border-black/20 ml-2">
                <button data-testid="cust-qty-dec" onClick={() => setQty(Math.max(1, qty - 1))} className="w-8 h-10 hover:bg-black/5">−</button>
                <span className="w-10 text-center text-sm font-semibold">{qty}</span>
                <button data-testid="cust-qty-inc" onClick={() => setQty(qty + 1)} className="w-8 h-10 hover:bg-black/5">+</button>
              </div>
            </div>
          </section>

          <section>
            <p className="label-tag mb-3"><Type size={12} className="inline mr-1" /> Add Text (₹149 per garment)</p>
            <Input data-testid="cust-text" value={text} maxLength={24} placeholder="e.g., Be Rare. Be Venvay." onChange={(e) => setText(e.target.value)} />
            {text && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Text color</Label>
                  <div className="flex gap-2 mt-1">
                    {["#F8F5EE", "#0A0A0A", "#8B2E2E", "#D4B25C"].map((c) => (
                      <button key={c} onClick={() => setTextColor(c)} className={`h-7 w-7 rounded-full border-2 ${textColor === c ? "border-[#8B2E2E]" : "border-black/10"}`} style={{ background: c }} />
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Text size</Label>
                  <Slider value={[textSize]} onValueChange={(v) => setTextSize(v[0])} min={20} max={80} step={2} className="mt-3" />
                </div>
              </div>
            )}
          </section>

          <section>
            <p className="label-tag mb-3"><Upload size={12} className="inline mr-1" /> Upload Design (₹199 per garment)</p>
            <div className="flex items-center gap-3">
              <input ref={fileRef} data-testid="cust-upload" type="file" accept="image/*" onChange={onUpload} className="hidden" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="cust-upload-btn">
                <Upload size={14} className="mr-2" /> Choose file
              </Button>
              {logo ? (
                <div className="flex items-center gap-2 text-sm">
                  <img src={logo.url} alt="" className="h-10 w-10 object-cover border" />
                  <span className="text-xs">{logo.name}</span>
                  <button onClick={() => setLogo(null)} data-testid="cust-remove-logo" className="text-xs text-[#8B2E2E] underline">Remove</button>
                </div>
              ) : (
                <span className="text-xs text-muted-foreground">PNG, JPG, SVG — max 3 MB</span>
              )}
            </div>
          </section>

          <section>
            <p className="label-tag mb-3">Print Placement</p>
            <RadioGroup value={placement} onValueChange={setPlacement} className="grid grid-cols-2 gap-2">
              {PLACEMENTS.map((p) => (
                <label key={p} className={`flex items-center gap-2 p-3 border cursor-pointer text-sm ${placement === p ? "border-[#8B2E2E] bg-[#8B2E2E]/5" : "border-black/10"}`}>
                  <RadioGroupItem data-testid={`place-${p}`} value={p} /> {p}
                </label>
              ))}
            </RadioGroup>
          </section>

          {/* Summary */}
          <section className="border-t border-black/10 pt-6">
            <div className="flex justify-between text-sm"><span>Garment ({garment.label})</span><span>₹{garment.base}</span></div>
            {text && <div className="flex justify-between text-sm"><span>Text print</span><span>₹149</span></div>}
            {logo && <div className="flex justify-between text-sm"><span>Design print</span><span>₹199</span></div>}
            <div className="flex justify-between text-sm"><span>Quantity</span><span>× {qty}</span></div>
            <div className="flex justify-between font-bold text-xl mt-3 pt-3 border-t border-black/10">
              <span>Total</span><span data-testid="cust-total">₹{total}</span>
            </div>
            <Button data-testid="cust-add-btn" onClick={addPrintedToCart} className="w-full mt-6 h-12 bg-[#8B2E2E] hover:bg-[#6f2323] uppercase tracking-widest text-xs">
              <ShoppingBag size={16} className="mr-2" /> Add to Bag
            </Button>
            <p className="text-center text-xs text-muted-foreground mt-3">
              <Sparkles size={11} className="inline text-[#8B2E2E]" /> Printed in-house · Ships in 3–5 days
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
